// ============================================================
//  RepairCore — Mini-game 3: Find and fix the broken equation
// ============================================================
import { PlaceValueQuestion } from '../../learning/math/PlaceValue.js';
import { audio } from '../../audio/AudioManager.js';
import { SaveManager } from '../../state/SaveManager.js';
import { XP_CORRECT, XP_HINT } from '../../learning/math/PlaceValue.js';
import type { MiniGameCallback } from './CrystalBuilder.js';

export class RepairCore {
  private scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  private question: PlaceValueQuestion;
  private onComplete: MiniGameCallback;
  private W: number;
  private H: number;
  private feedbackText!: Phaser.GameObjects.Text;
  private attempts = 0;
  private hintUsed = false;

  constructor(scene: Phaser.Scene, question: PlaceValueQuestion, onComplete: MiniGameCallback) {
    this.scene = scene;
    this.question = question;
    this.onComplete = onComplete;
    this.W = scene.scale.width;
    this.H = scene.scale.height;
    this.create();
  }

  private create() {
    const { W, H } = this;
    const { target, hundreds, tens, ones, repairField, repairCorrect, repairWrong, choices } = this.question;

    this.container = this.scene.add.container(0, 0);

    // Overlay + Panel
    const overlay = this.scene.add.graphics();
    overlay.fillStyle(0x000010, 0.88); overlay.fillRect(0, 0, W, H);
    const panel = this.scene.add.graphics();
    panel.fillStyle(0x0d0230, 0.97); panel.fillRoundedRect(W * 0.06, H * 0.05, W * 0.88, H * 0.9, 20);
    panel.lineStyle(1.5, 0x3d2060, 0.8); panel.strokeRoundedRect(W * 0.06, H * 0.05, W * 0.88, H * 0.9, 20);

    const title = this.scene.add.text(W * 0.5, H * 0.1, '🔧 Repair the Number Core!', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '22px', fontStyle: 'bold', color: '#ff8c00',
      stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5, 0.5);

    const instr = this.scene.add.text(W * 0.5, H * 0.175,
      `The machine has a broken number! Find the wrong part and fix it.`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '15px', color: '#ccddff', align: 'center',
    }).setOrigin(0.5, 0.5);

    // Holographic display panel
    const holoG = this.scene.add.graphics();
    holoG.fillStyle(0x080030, 0.95); holoG.fillRoundedRect(W * 0.08, H * 0.22, W * 0.84, H * 0.26, 16);
    holoG.lineStyle(2, 0x00e5ff, 0.4); holoG.strokeRoundedRect(W * 0.08, H * 0.22, W * 0.84, H * 0.26, 16);
    // Scan line effect
    for (let y = 0; y < H * 0.26; y += 8) {
      holoG.lineStyle(1, 0x00e5ff, 0.04);
      holoG.lineBetween(W * 0.08, H * 0.22 + y, W * 0.08 + W * 0.84, H * 0.22 + y);
    }

    const equLine1 = this.scene.add.text(W * 0.5, H * 0.265, `${target}  =`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '32px', fontStyle: 'bold', color: '#ffffff',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5);

    // Three parts of the decomposition
    const parts = [
      { field: 'hundreds', val: repairField === 'hundreds' ? repairWrong : hundreds, unit: 'hundreds', unitSingle: 'hundred', unitVal: 100 },
      { field: 'tens',     val: repairField === 'tens'     ? repairWrong : tens,     unit: 'tens',     unitSingle: 'ten',     unitVal: 10 },
      { field: 'ones',     val: repairField === 'ones'     ? repairWrong : ones,     unit: 'ones',     unitSingle: 'one',     unitVal: 1 },
    ] as const;

    const partXs = [W * 0.22, W * 0.5, W * 0.78];
    parts.forEach(({ field, val, unit, unitSingle }, i) => {
      const isBroken = field === repairField;
      const partG = this.scene.add.graphics();
      const px = partXs[i], py = H * 0.345;
      const boxW = W * 0.22, boxH = H * 0.1;

      // Box
      partG.fillStyle(isBroken ? 0x3a0000 : 0x0d0230, 0.9);
      partG.fillRoundedRect(px - boxW / 2, py - boxH / 2, boxW, boxH, 10);
      partG.lineStyle(2, isBroken ? 0xff4444 : 0x3d2060, isBroken ? 1 : 0.6);
      partG.strokeRoundedRect(px - boxW / 2, py - boxH / 2, boxW, boxH, 10);

      // "BROKEN" label
      if (isBroken) {
        const brokenLabel = this.scene.add.text(px, py - boxH / 2 - 10, '⚠ BROKEN', {
          fontFamily: '"Nunito", Arial, sans-serif', fontSize: '11px', color: '#ff4444',
          stroke: '#000', strokeThickness: 1,
        }).setOrigin(0.5, 0.5);
        this.container.add(brokenLabel);
      }

      const numTxt = this.scene.add.text(px, py - 8, `${val}`, {
        fontFamily: '"Nunito", Arial, sans-serif',
        fontSize: '26px', fontStyle: 'bold',
        color: isBroken ? '#ff6666' : '#ffffff',
        stroke: '#000', strokeThickness: 2,
      }).setOrigin(0.5, 0.5);
      const unitTxt = this.scene.add.text(px, py + 14, (val === 1 ? unitSingle : unit), {
        fontFamily: '"Nunito", Arial, sans-serif',
        fontSize: '12px', color: isBroken ? '#ff9999' : '#8888bb',
      }).setOrigin(0.5, 0.5);

      // Separator
      if (i < 2) {
        this.scene.add.text(partXs[i] + W * 0.14, py, '+', {
          fontFamily: '"Nunito", Arial, sans-serif', fontSize: '24px', color: '#666688',
        }).setOrigin(0.5, 0.5);
      }

      this.container.add([partG, numTxt, unitTxt]);
    });

    // Question prompt
    const fieldLabel = repairField ?? 'ones';
    const unitWord = fieldLabel === 'hundreds' ? `hundred${(repairCorrect ?? 0) !== 1 ? 's' : ''}` :
                     fieldLabel === 'tens'     ? `ten${(repairCorrect ?? 0) !== 1 ? 's' : ''}` :
                                                 `one${(repairCorrect ?? 0) !== 1 ? 's' : ''}`;
    const prompt = this.scene.add.text(W * 0.5, H * 0.54,
      `The ${fieldLabel} are wrong! What is the correct number of ${unitWord} in  ${target}?`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '14px', color: '#ccddff', align: 'center', wordWrap: { width: W * 0.82 },
    }).setOrigin(0.5, 0.5);

    // Answer choices
    const allChoices = choices ?? [0, 1, 2, 3];
    const choiceXs = allChoices.length === 4
      ? [W * 0.2, W * 0.4, W * 0.6, W * 0.8]
      : [W * 0.25, W * 0.5, W * 0.75];

    allChoices.forEach((choice, i) => {
      const isCorrect = choice === repairCorrect;
      const cx = choiceXs[i], cy = H * 0.68;

      const choiceG = this.scene.add.graphics();
      choiceG.fillStyle(0x0d0230, 1); choiceG.fillRoundedRect(cx - 45, cy - 32, 90, 64, 12);
      choiceG.lineStyle(2, 0x3d2060, 0.7); choiceG.strokeRoundedRect(cx - 45, cy - 32, 90, 64, 12);

      const choiceTxt = this.scene.add.text(cx, cy, `${choice}`, {
        fontFamily: '"Nunito", Arial, sans-serif',
        fontSize: '26px', fontStyle: 'bold', color: '#ffffff',
        stroke: '#000', strokeThickness: 2,
      }).setOrigin(0.5, 0.5);

      choiceG.setInteractive(new Phaser.Geom.Rectangle(cx - 45, cy - 32, 90, 64), Phaser.Geom.Rectangle.Contains);
      choiceG.on('pointerover', () => {
        choiceG.clear();
        choiceG.fillStyle(0x1e0d30, 1); choiceG.fillRoundedRect(cx - 45, cy - 32, 90, 64, 12);
        choiceG.lineStyle(2, 0x9b4dff, 1); choiceG.strokeRoundedRect(cx - 45, cy - 32, 90, 64, 12);
        this.scene.tweens.add({ targets: choiceTxt, scaleX: 1.1, scaleY: 1.1, duration: 100 });
        audio.click();
      });
      choiceG.on('pointerout', () => {
        choiceG.clear();
        choiceG.fillStyle(0x0d0230, 1); choiceG.fillRoundedRect(cx - 45, cy - 32, 90, 64, 12);
        choiceG.lineStyle(2, 0x3d2060, 0.7); choiceG.strokeRoundedRect(cx - 45, cy - 32, 90, 64, 12);
        this.scene.tweens.add({ targets: choiceTxt, scaleX: 1, scaleY: 1, duration: 100 });
      });
      choiceG.on('pointerdown', () => this.selectChoice(choice, isCorrect, choiceG, choiceTxt, cx, cy));

      this.container.add([choiceG, choiceTxt]);
    });

    // Hint
    const hintBtn = this.makeButton(W * 0.5, H * 0.82, '💡 Hint', '#ffeb3b', () => this.showHint());

    this.feedbackText = this.scene.add.text(W * 0.5, H * 0.9, '', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '15px', color: '#00e676', stroke: '#000', strokeThickness: 2, align: 'center',
    }).setOrigin(0.5, 0.5);

    this.container.add([overlay, panel, title, instr, holoG, equLine1, prompt, hintBtn, this.feedbackText]);
  }

  private selectChoice(
    choice: number,
    isCorrect: boolean,
    choiceG: Phaser.GameObjects.Graphics,
    choiceTxt: Phaser.GameObjects.Text,
    cx: number, cy: number,
  ) {
    this.attempts++;
    if (isCorrect) {
      // Flash green
      choiceG.clear();
      choiceG.fillStyle(0x003300, 1); choiceG.fillRoundedRect(cx - 45, cy - 32, 90, 64, 12);
      choiceG.lineStyle(3, 0x00e676, 1); choiceG.strokeRoundedRect(cx - 45, cy - 32, 90, 64, 12);
      choiceTxt.setStyle({ color: '#00e676' });

      SaveManager.recordAnswer(this.question.skillId, true);
      SaveManager.addXP(this.hintUsed ? XP_HINT : XP_CORRECT);
      audio.correct();

      this.feedbackText.setStyle({ color: '#00e676' });
      this.feedbackText.setText('✓ Correct! The core is repaired!');

      // Spark burst
      try {
        const emitter = this.scene.add.particles(cx, cy, 'particle_star', {
          speed: { min: 60, max: 160 },
          angle: { min: 0, max: 360 },
          scale: { start: 1, end: 0 },
          lifespan: 700,
          quantity: 14,
          tint: [0xffd700, 0x00e676, 0x00e5ff],
        });
        this.scene.time.delayedCall(250, () => emitter.stop());
        this.scene.time.delayedCall(900, () => emitter.destroy());
      } catch { /* ok */ }

      this.scene.time.delayedCall(1300, () => {
        this.destroy();
        this.onComplete(true);
      });
    } else {
      // Flash red
      SaveManager.recordAnswer(this.question.skillId, false);
      audio.wrong();
      choiceG.clear();
      choiceG.fillStyle(0x330000, 1); choiceG.fillRoundedRect(cx - 45, cy - 32, 90, 64, 12);
      choiceG.lineStyle(2, 0xff4444, 1); choiceG.strokeRoundedRect(cx - 45, cy - 32, 90, 64, 12);
      choiceTxt.setStyle({ color: '#ff8888' });

      this.feedbackText.setStyle({ color: '#ff8888' });
      this.feedbackText.setText('Not quite. Look at the number again and think!');

      this.scene.time.delayedCall(1200, () => {
        // Reset button color
        choiceG.clear();
        choiceG.fillStyle(0x0d0230, 1); choiceG.fillRoundedRect(cx - 45, cy - 32, 90, 64, 12);
        choiceG.lineStyle(2, 0x3d2060, 0.7); choiceG.strokeRoundedRect(cx - 45, cy - 32, 90, 64, 12);
        choiceTxt.setStyle({ color: '#ffffff' });
        if (this.attempts >= 2 && !this.hintUsed) {
          this.feedbackText.setStyle({ color: '#ffeb3b' });
          this.feedbackText.setText('💡 Tap Hint to get help!');
        } else {
          this.feedbackText.setText('');
        }
      });
    }
  }

  private showHint() {
    this.hintUsed = true;
    SaveManager.addXP(XP_HINT);
    const { target, repairField, repairCorrect } = this.question;
    const unitWord = repairField === 'hundreds' ? 'hundreds' : repairField === 'tens' ? 'tens' : 'ones';
    this.feedbackText.setStyle({ color: '#ffeb3b' });
    this.feedbackText.setText(`Hint: ${target} has ${repairCorrect} ${unitWord}.`);
    audio.hint();
  }

  private makeButton(x: number, y: number, label: string, color: string, cb: () => void) {
    const btn = this.scene.add.text(x, y, label, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '16px', fontStyle: 'bold', color,
      backgroundColor: '#0d0230', padding: { x: 16, y: 8 },
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5).setInteractive({ cursor: 'pointer' });
    btn.on('pointerover', () => { this.scene.tweens.add({ targets: btn, scaleX: 1.06, scaleY: 1.06, duration: 100 }); audio.click(); });
    btn.on('pointerout', () => this.scene.tweens.add({ targets: btn, scaleX: 1, scaleY: 1, duration: 100 }));
    btn.on('pointerdown', cb);
    return btn;
  }

  destroy() { this.container.destroy(); }
}
